// ./components/AuthPage/AuthPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import md5 from 'md5'; // 需要先安装： npm install md5
import './AuthPage.css';

import { fetchMathApiNoAuth } from "../../utils/mathServerHttp";

function AuthPage() {
    const navigate = useNavigate();

    // ---------------------------
    // 1. 页面加载时检查登录状态
    // ---------------------------
    useEffect(() => {
        (async () => {
            try {
                await fetchMathApiNoAuth('/api/user/info');
                // 如果返回成功，说明已登录
                navigate('/unit');
                // 如果是 401，则说明未登录，什么都不做，留在当前页
            } catch (error) {
                // 如果请求出错，按未登录处理，什么也不做
            }
        })();
    }, [navigate]);

    // ---------------------------
    // 2. 用于区分当前是"登录"还是"注册"
    // ---------------------------
    const [isLogin, setIsLogin] = useState(true);

    // ---------------------------
    // 3. 登录相关字段 & 错误信息
    // ---------------------------
    const [loginAccount, setLoginAccount] = useState('');
    const [loginAccountError, setLoginAccountError] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [loginPasswordError, setLoginPasswordError] = useState('');

    // ---------------------------
    // 4. 注册相关字段 & 错误信息
    // ---------------------------
    const [registerAccount, setRegisterAccount] = useState('');
    const [registerAccountError, setRegisterAccountError] = useState('');
    const [registerPassword, setRegisterPassword] = useState('');
    const [registerPasswordError, setRegisterPasswordError] = useState('');
    const [registerPassword2, setRegisterPassword2] = useState('');
    const [registerPassword2Error, setRegisterPassword2Error] = useState('');
    const [nickname, setNickname] = useState('');
    const [nicknameError, setNicknameError] = useState('');

    // ---------------------------
    // 【新增】年级相关字段 & 错误信息
    // ---------------------------
    const [grade, setGrade] = useState('');
    const [gradeError, setGradeError] = useState('');
    const [gradeOptions, setGradeOptions] = useState([]); // 下拉框可选年级列表

    // ---------------------------
    // 5. 通用错误提示（接口返回的 message）
    // ---------------------------
    const [errorMsg, setErrorMsg] = useState('');

    // ---------------------------
    // 6. 正则（根据需求可自行调整）
    // ---------------------------
    // 账号：6-20位，英文或数字
    const accountRegex = /^[A-Za-z0-9]{6,20}$/;
    // 密码：6-20位，支持字母/数字/常见特殊字符
    const passwordRegex = /^[A-Za-z0-9`~!@#$%^&*()_\-+={}[\]|:;"'<>,.?/]{6,20}$/;
    // 昵称：1-20位，中英文/数字/常见特殊字符
    const nicknameRegex = /^[\u4e00-\u9fa5A-Za-z0-9`~!@#$%^&*()_\-+={}[\]|:;"'<>,.?/]{1,20}$/;

    // ---------------------------
    // 7. 输入处理 & 实时校验
    // ---------------------------
    const handleLoginAccountChange = (e) => {
        const value = e.target.value;
        setLoginAccount(value);
        setLoginAccountError('');
        setErrorMsg(''); // 清空接口错误
    };

    const handleLoginPasswordChange = (e) => {
        const value = e.target.value;
        setLoginPassword(value);
        setLoginPasswordError('');
        setErrorMsg(''); // 清空接口错误
    };

    const handleRegisterAccountChange = (e) => {
        const value = e.target.value;
        setRegisterAccount(value);
        if (!accountRegex.test(value)) {
            setRegisterAccountError('账号格式不正确，请输入6~20位英文或数字！');
        } else {
            setRegisterAccountError('');
        }
    };

    const handleRegisterPasswordChange = (e) => {
        const value = e.target.value;
        setRegisterPassword(value);
        if (!passwordRegex.test(value)) {
            setRegisterPasswordError('密码格式不正确，请输入6~20位英文/数字/特殊字符！');
        } else {
            setRegisterPasswordError('');
        }
    };

    // 二次输入密码
    const handleRegisterPassword2Change = (e) => {
        const value = e.target.value;
        setRegisterPassword2(value);
        // 只要和第一个密码不一致就提示
        if (value !== registerPassword) {
            setRegisterPassword2Error('两次密码输入不一致！');
        } else {
            setRegisterPassword2Error('');
        }
    };

    const handleNicknameChange = (e) => {
        const value = e.target.value;
        setNickname(value);
        if (!nicknameRegex.test(value)) {
            setNicknameError('昵称格式不正确，请输入1~20位字符！');
        } else {
            setNicknameError('');
        }
    };

    // 【新增】年级下拉选择
    const handleGradeChange = (e) => {
        setGrade(e.target.value);
        setGradeError('');
        setErrorMsg('');
    };

    // ---------------------------
    // 8. 登录提交
    // ---------------------------
    const handleLogin = async () => {
        setErrorMsg(''); // 清空接口错误

        if (!accountRegex.test(loginAccount)) {
            setLoginAccountError('账号格式不正确，请输入6~20位英文或数字！');
        }
        if (!passwordRegex.test(loginPassword)) {
            setLoginPasswordError('密码格式不正确，请输入6~20位英文/数字/特殊字符！');
        }

        // 若任一字段有错误，则不提交
        if (loginAccountError || loginPasswordError) {
            return;
        }

        // 若仍有空值，也不提交 (可选)
        if (!loginAccount || !loginPassword) {
            setErrorMsg('请填写完整的账号和密码！');
            return;
        }

        // 生成 timestamp 和 token
        const timestamp = Date.now();
        const token = md5(`${loginAccount}_${loginPassword}_${timestamp}`);

        try {
            await fetchMathApiNoAuth('/api/user/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    account: loginAccount,
                    timestamp: String(timestamp),
                    token,
                }).toString(),
            });

            // 登录成功，跳转到 /unit
            navigate('/unit');
        } catch (error) {
            setErrorMsg(error.message || '登录失败！');
        }
    };

    // ---------------------------
    // 9. 注册提交
    // ---------------------------
    const handleRegister = async () => {
        setErrorMsg('');

        // 若任一字段有错误，则不提交
        if (
            registerAccountError ||
            registerPasswordError ||
            registerPassword2Error ||
            nicknameError ||
            gradeError
        ) {
            return;
        }

        // 若有空值，也不提交 (可选)
        if (!registerAccount) {
            setErrorMsg('请填写账号信息！');
            return;
        }

        if (!registerPassword || !registerPassword2) {
            setErrorMsg('请填写密码！');
            return;
        }

        if (!nickname) {
            setErrorMsg('请填写昵称！');
            return;
        }

        if (!grade) {
            setErrorMsg('请选择年级！');
            return;
        }

        // 再次检查两次密码是否相同
        if (registerPassword !== registerPassword2) {
            setRegisterPassword2Error('两次密码输入不一致！');
            return;
        }

        try {
            await fetchMathApiNoAuth('/api/user/register', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    account: registerAccount,
                    password: registerPassword,
                    nickname: nickname,
                    grade: grade, // 传递年级
                }),
            });
            // 注册成功，跳转到 /unit
            navigate('/unit');
        } catch (error) {
            setErrorMsg(error.message || '注册失败！');
        }
    };

    // -----------------------------------------------------------
    // 10. 引用（Ref）和回车事件，实现在输入完一个框按回车跳到下一个
    // -----------------------------------------------------------
    const loginAccountRef = useRef(null);
    const loginPasswordRef = useRef(null);

    const registerAccountRef = useRef(null);
    const registerPasswordRef = useRef(null);
    const registerPassword2Ref = useRef(null);
    const nicknameRef = useRef(null);

    // 登录表单：回车自动跳转到下一个输入框或提交
    const handleKeyDownLoginAccount = (e) => {
        if (e.key === 'Enter') {
            loginPasswordRef.current?.focus();
        }
    };
    const handleKeyDownLoginPassword = (e) => {
        if (e.key === 'Enter') {
            handleLogin();
        }
    };

    // 注册表单：回车自动跳转或提交
    const handleKeyDownRegisterAccount = (e) => {
        if (e.key === 'Enter') {
            registerPasswordRef.current?.focus();
        }
    };
    const handleKeyDownRegisterPassword = (e) => {
        if (e.key === 'Enter') {
            registerPassword2Ref.current?.focus();
        }
    };
    const handleKeyDownRegisterPassword2 = (e) => {
        if (e.key === 'Enter') {
            nicknameRef.current?.focus();
        }
    };

    // ---------------------------
    // 【新增】在页面加载时，从后端接口获取年级列表
    // ---------------------------
    useEffect(() => {
        (async () => {
            try {
                // 调用后端接口，获取年级列表
                const data = await fetchMathApiNoAuth("/api/grade/list");
                // 将返回的数据映射为下拉可用的格式，如：{ value: code, label: name }
                const mapped = data.map(item => ({
                    value: String(item.code),
                    label: item.name
                }));
                setGradeOptions(mapped);
            } catch (err) {
                // 若接口请求失败，可以在这里做一些容错处理
                console.error("获取年级列表失败：", err);
            }
        })();
    }, []);

    return (
        <div className="auth-page">
        <div className="auth-container">
            <h2 className="auth-title">欢迎来到多多国</h2>

            <div className="auth-forms">
                {/* 登录表单 */}
                <div className={`auth-form login-form ${isLogin ? '' : 'hidden'}`}>
                    <div className="form-group">
                        <label>账号：</label>
                        <input
                            type="text"
                            ref={loginAccountRef}
                            value={loginAccount}
                            onChange={handleLoginAccountChange}
                            onKeyDown={handleKeyDownLoginAccount}
                            placeholder="6-20位英文或数字"
                        />
                        {/* 如果有错误，显示在下面 */}
                        {loginAccountError && (
                            <div className="input-error">{loginAccountError}</div>
                        )}
                    </div>
                    <div className="form-group">
                        <label>密码：</label>
                        <input
                            type="password"
                            ref={loginPasswordRef}
                            value={loginPassword}
                            onChange={handleLoginPasswordChange}
                            onKeyDown={handleKeyDownLoginPassword}
                            placeholder="6-20位可含特殊字符"
                        />
                        {loginPasswordError && (
                            <div className="input-error">{loginPasswordError}</div>
                        )}
                    </div>
                    <button className="submit-btn" onClick={handleLogin}>
                        登录
                    </button>
                </div>

                {/* 注册表单 */}
                <div className={`auth-form register-form ${isLogin ? 'hidden' : ''}`}>
                    <div className="form-group">
                        <label>账号：</label>
                        <input
                            type="text"
                            ref={registerAccountRef}
                            value={registerAccount}
                            onChange={handleRegisterAccountChange}
                            onKeyDown={handleKeyDownRegisterAccount}
                            placeholder="6-20位英文或数字"
                        />
                        {registerAccountError && (
                            <div className="input-error">{registerAccountError}</div>
                        )}
                    </div>
                    <div className="form-group">
                        <label>密码：</label>
                        <input
                            type="password"
                            ref={registerPasswordRef}
                            value={registerPassword}
                            onChange={handleRegisterPasswordChange}
                            onKeyDown={handleKeyDownRegisterPassword}
                            placeholder="6-20位可含特殊字符"
                        />
                        {registerPasswordError && (
                            <div className="input-error">{registerPasswordError}</div>
                        )}
                    </div>
                    <div className="form-group">
                        <label>确认密码：</label>
                        <input
                            type="password"
                            ref={registerPassword2Ref}
                            value={registerPassword2}
                            onChange={handleRegisterPassword2Change}
                            onKeyDown={handleKeyDownRegisterPassword2}
                            placeholder="请再次输入密码"
                        />
                        {registerPassword2Error && (
                            <div className="input-error">{registerPassword2Error}</div>
                        )}
                    </div>
                    <div className="form-group">
                        <label>昵称：</label>
                        <input
                            type="text"
                            ref={nicknameRef}
                            value={nickname}
                            onChange={handleNicknameChange}
                            placeholder="1-20位字符"
                        />
                        {nicknameError && (
                            <div className="input-error">{nicknameError}</div>
                        )}
                    </div>

                    {/* 【新增】年级选择下拉框 */}
                    <div className="form-group">
                        <label>年级：</label>
                        <select value={grade} onChange={handleGradeChange}>
                            <option value="">请选择年级</option>
                            {gradeOptions.map((g) => (
                                <option key={g.value} value={g.value}>
                                    {g.label}
                                </option>
                            ))}
                        </select>
                        {gradeError && (
                            <div className="input-error">{gradeError}</div>
                        )}
                    </div>

                    <button className="submit-btn" onClick={handleRegister}>
                        注册
                    </button>
                </div>
            </div>

            {/* 错误提示（接口返回的 message） */}
            {errorMsg && <div className="error-msg">{errorMsg}</div>}

            {/* 切换按钮 */}
            <div className="switch-area">
                {isLogin ? (
                    <p>
                        还没有账号？
                        <button onClick={() => setIsLogin(false)}>去注册</button>
                    </p>
                ) : (
                    <p>
                        已有账号？
                        <button onClick={() => setIsLogin(true)}>去登录</button>
                    </p>
                )}
            </div>
        </div>

            <footer className="auth-beian">
                <a
                    href="https://beian.mps.gov.cn/#/query/webSearch?code=11010802047862"
                    rel="noreferrer"
                    target="_blank"
                >
                    <img
                        className="auth-beian-icon"
                        src="/beian-icon.png"
                        alt=""
                    />
                    京公网安备11010802047862号
                </a>
            </footer>
        </div>
    );
}

export default AuthPage;
